import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { cn } from '@/lib/utils';

export type PeriodPreset = 'this_month' | 'last_month' | '3m' | '6m' | 'all' | 'custom';

export interface PeriodRange {
  dateFrom?: string;  // YYYY-MM-DD
  dateTo?: string;    // YYYY-MM-DD
  preset: PeriodPreset;
}

interface PeriodFilterProps {
  value: PeriodRange;
  onChange: (range: PeriodRange) => void;
  className?: string;
  label?: string;
  /** Hide the "All Time" pill */
  hideAll?: boolean;
  /** Restrict to specific presets only */
  allowedPresets?: PeriodPreset[];
}

const PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: '3m', label: '3 Months' },
  { key: '6m', label: '6 Months' },
  { key: 'all', label: 'All Time' },
  { key: 'custom', label: 'Custom' },
];

function computeRange(preset: PeriodPreset): { dateFrom?: string; dateTo?: string } {
  const now = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  const fmt = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

  if (preset === 'this_month') {
    const from = new Date(now.getFullYear(), now.getMonth(), 1);
    const to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { dateFrom: fmt(from), dateTo: fmt(to) };
  }
  if (preset === 'last_month') {
    const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const to = new Date(now.getFullYear(), now.getMonth(), 0);
    return { dateFrom: fmt(from), dateTo: fmt(to) };
  }
  if (preset === '3m') {
    const from = new Date(now);
    from.setMonth(from.getMonth() - 3);
    return { dateFrom: fmt(from), dateTo: fmt(now) };
  }
  if (preset === '6m') {
    const from = new Date(now);
    from.setMonth(from.getMonth() - 6);
    return { dateFrom: fmt(from), dateTo: fmt(now) };
  }
  return {};  // 'all' or 'custom' — no bounds
}

export const PeriodFilter: React.FC<PeriodFilterProps> = ({
  value,
  onChange,
  className,
  label,
  hideAll = false,
  allowedPresets,
}) => {
  const [showCustom, setShowCustom] = useState(value.preset === 'custom');

  const handlePreset = (preset: PeriodPreset) => {
    setShowCustom(preset === 'custom');
    if (preset === 'custom') {
      onChange({ preset, dateFrom: value.dateFrom, dateTo: value.dateTo });
    } else {
      const range = computeRange(preset);
      onChange({ preset, ...range });
    }
  };

  let visiblePresets = hideAll ? PRESETS.filter(p => p.key !== 'all') : PRESETS;
  if (allowedPresets) {
    visiblePresets = visiblePresets.filter(p => allowedPresets.includes(p.key));
  }

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <span className="text-sm font-medium text-gray-700">{label}</span>}
      <div className="flex flex-wrap items-center gap-2">
        {visiblePresets.map(({ key, label: pLabel }) => (
          <button
            key={key}
            type="button"
            onClick={() => handlePreset(key)}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-full border transition-all',
              value.preset === key
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-600 border-gray-300 hover:border-blue-400 hover:text-blue-600'
            )}
            aria-pressed={value.preset === key}
          >
            {pLabel}
          </button>
        ))}

        {/* Custom date inputs */}
        {showCustom && (
          <div className="flex items-center gap-1.5 flex-wrap">
            <Calendar className="h-3.5 w-3.5 text-gray-400" />
            <input
              type="date"
              value={value.dateFrom || ''}
              onChange={(e) => onChange({ ...value, dateFrom: e.target.value || undefined })}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="Start date"
            />
            <span className="text-gray-400 text-xs">–</span>
            <input
              type="date"
              value={value.dateTo || ''}
              onChange={(e) => onChange({ ...value, dateTo: e.target.value || undefined })}
              className="px-2 py-1 text-xs border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
              aria-label="End date"
            />
          </div>
        )}
      </div>
    </div>
  );
};

/** Helper: get current month's default range */
export function defaultPeriod(): PeriodRange {
  const range = computeRange('this_month');
  return { preset: 'this_month', ...range };
}
